import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { t, lang, setLang, type Lang } from "@/lib/i18n";
import { internesZiel } from "@/lib/ziel";
import type { OidcPublicStatus } from "@/lib/types";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sso, setSso] = useState<OidcPublicStatus | null>(null);

  const from = internesZiel((location.state as { from?: string } | null)?.from);

  // SSO-Rücksprung: der Callback leitet auf /login#sso_token=… bzw.
  // #sso_error=… weiter. Token übernehmen, Fragment aus der URL entfernen.
  useEffect(() => {
    if (!location.hash || location.hash.length < 2) return;
    const params = new URLSearchParams(location.hash.slice(1));
    const token = params.get("sso_token");
    const ssoError = params.get("sso_error");
    if (token) {
      setToken(token);
      // Full-Page-Redirect statt SPA-navigate: nach dem externen SSO-Redirect
      // ist der AuthProvider-State noch nicht aktualisiert (Token kommt via
      // Event, nicht synchron). Ein SPA-navigate zu einer geschützten Route
      // würde deshalb kurz auf /login zurückspringen. Ein echter Reload liest
      // das Token frisch aus dem localStorage → RequireAuth sieht es sofort.
      window.location.replace(from);
      return;
    } else if (ssoError) {
      setError(ssoError);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [location.hash, from, navigate]);

  // Ob der SSO-Button angezeigt wird (öffentlicher, unauthentifizierter Endpoint).
  useEffect(() => {
    let alive = true;
    api
      .get<OidcPublicStatus>("/api/auth/oidc/status")
      .then((s) => alive && setSso(s))
      .catch(() => alive && setSso(null));
    return () => {
      alive = false;
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const resp = await auth.login(username, password);
      if (resp.success && resp.token) {
        try {
          navigate(from, { replace: true });
        } catch {
          // Angemeldet ist man an dieser Stelle schon. Ein Ziel, das der
          // Browser nicht annimmt, darf nicht wie eine gescheiterte Anmeldung
          // aussehen.
          navigate("/logs", { replace: true });
        }
      } else {
        setError(resp.message || t("Login fehlgeschlagen"));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("Login fehlgeschlagen"));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex h-full items-center justify-center bg-background p-6">
      <form
        onSubmit={onSubmit}
        className="w-[min(380px,100%)] rounded-lg border border-border bg-card p-6 shadow-sm"
      >
        <div className="mb-5 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-base font-medium text-primary-foreground">
            L
          </div>
          <h1 className="text-lg font-medium">Leif</h1>
        </div>

        <label className="block text-xs font-medium text-muted-foreground">{t("Benutzername")}</label>
        <input
          type="text"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          className="mb-3 mt-1 h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />

        <label className="block text-xs font-medium text-muted-foreground">{t("Passwort")}</label>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="mb-4 mt-1 h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />

        {error ? (
          <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={pending}
          className="h-9 w-full rounded-md bg-primary text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {pending ? t("Anmelden…") : t("Anmelden")}
        </button>

        {sso?.enabled ? (
          <>
            <div className="my-4 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {t("oder")}
              <span className="h-px flex-1 bg-border" />
            </div>
            <a
              href="/api/auth/oidc/login"
              className="flex h-9 w-full items-center justify-center rounded-md border border-border bg-background text-sm font-medium hover:bg-muted"
            >
              {sso.button_label || t("Mit SSO anmelden")}
            </a>
          </>
        ) : null}

        {/* Sprachwahl — vor dem Login wichtig (FR/EN-Werke), danach im Header. */}
        <div className="mt-5 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
          {(["de", "en", "fr"] as Lang[]).map((l, i) => (
            <span key={l} className="flex items-center gap-1">
              {i > 0 ? <span aria-hidden>·</span> : null}
              <button
                type="button"
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={
                  "rounded px-1 py-0.5 uppercase hover:text-foreground " +
                  (lang === l ? "font-semibold text-foreground" : "")
                }
              >
                {l}
              </button>
            </span>
          ))}
        </div>
      </form>
    </div>
  );
}
